import { useEffect, useState } from 'react';
import { ChatCircleIcon, ListIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { IconButton, Spinner, useOverlayState, useToast } from '@smworks-cz/ui-kit';
import type { ConversationDTO, ViewDTO } from '@sam/shared';
import { TopTitle } from './components/TopTitle';
import { useQuery } from '@tanstack/react-query';
import { Sidebar } from './components/Sidebar';
import { SettingsModal } from './components/SettingsModal';
import { Search } from './components/Search';
import { LoginScreen } from './components/Login';
import { Home } from './pages/Home';
import { Chat } from './pages/Chat';
import { Transcripts } from './pages/Transcripts';
import { View } from './pages/View';
import { keys, queryClient, useConversations, useMe, useViews } from './lib/queries';
import { navigate, useRoute } from './lib/router';
import { applyTheme, watchSystemTheme } from './lib/theme';
import { api, ApiError } from './lib/api';

export function App() {
  const me = useMe();
  const route = useRoute();
  const [sideOpen, setSideOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sam-side-collapsed') === '1';
    } catch {
      return false;
    }
  });
  const toggleSide = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem('sam-side-collapsed', c ? '0' : '1');
      } catch {
        /* soukromé okno */
      }
      return !c;
    });
  const [settings, setSettings] = useState(false);
  const { toast } = useToast();
  // Návrat z propojení Microsoft 365 (/auth/m365/callback → /?m365=…): oznámit výsledek a uklidit adresu.
  useEffect(() => {
    const r = new URLSearchParams(window.location.search).get('m365');
    if (!r) return;
    window.history.replaceState(null, '', window.location.pathname);
    const msg: Record<string, [string, 'success' | 'error']> = {
      ok: ['Pošta a soubory z Microsoft 365 jsou propojené.', 'success'],
      consent: ['Microsoft chce souhlas správce s oprávněními aplikace. Požádejte správce tenantu.', 'error'],
      other_account: ['Propojit jde jen stejný Microsoft účet, kterým jste přihlášeni v appce.', 'error'],
      not_configured: ['Propojení s Microsoft 365 není na serveru nastavené.', 'error'],
      error: ['Propojení s Microsoft 365 se nepodařilo. Zkuste to znovu.', 'error'],
    };
    const [title, variant] = msg[r] ?? msg.error;
    toast({ variant, title });
    void queryClient.invalidateQueries({ queryKey: ['m365'] });
  }, [toast]);
  const [search, setSearch] = useState(false);
  const overlay = useOverlayState();
  const convs = useConversations();
  const views = useViews();
  const unauth = me.error instanceof ApiError && me.error.status === 401;
  const authMode = useQuery({
    queryKey: ['auth-mode'],
    enabled: unauth,
    queryFn: () => api<{ mode: 'microsoft' | 'dev' }>('GET', '/auth/mode'),
  });

  useEffect(() => {
    if (me.data) applyTheme(me.data.theme);
  }, [me.data?.theme]);
  useEffect(() => watchSystemTheme(() => me.data?.theme ?? 'auto'), [me.data?.theme]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (overlay.open || !(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k === 'k') {
        e.preventDefault();
        setSearch(true);
      } else if (k === 'b' && !e.shiftKey) {
        e.preventDefault();
        toggleSide();
      } else if (k === 'o' && e.shiftKey) {
        // Nový chat jako v Claude (Ctrl+Shift+O).
        e.preventDefault();
        navigate('/');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [overlay.open]);

  if (unauth) {
    return authMode.data ? (
      <LoginScreen mode={authMode.data.mode} />
    ) : (
      <div className="page-center">
        <Spinner />
      </div>
    );
  }
  if (me.isLoading || !me.data) {
    return <div className="page-center">{me.error ? <p className="bad">Aplikace teď nejde načíst: {(me.error as Error).message}</p> : <Spinner />}</div>;
  }

  const title =
    route.t === 'chat'
      ? (convs.data?.find((c) => c.id === route.id)?.title ?? 'Chat')
      : route.t === 'view'
        ? (views.data?.find((v) => v.id === route.id)?.title ?? 'Pohled')
        : route.t === 'transcripts'
          ? 'Přepisy'
          : 'Nový chat';

  const rename =
    route.t === 'chat'
      ? async (t: string) => {
          await api('PATCH', `/conversations/${route.id}`, { title: t });
          queryClient.setQueryData<ConversationDTO[]>(keys.conversations, (old) => old?.map((x) => (x.id === route.id ? { ...x, title: t } : x)));
          void queryClient.invalidateQueries({ queryKey: keys.conversation(route.id) });
        }
      : route.t === 'view'
        ? async (t: string) => {
            await api('PATCH', `/views/${route.id}`, { title: t });
            queryClient.setQueryData<ViewDTO[]>(keys.views, (old) => old?.map((x) => (x.id === route.id ? { ...x, title: t } : x)));
            void queryClient.invalidateQueries({ queryKey: keys.view(route.id) });
          }
        : undefined;

  return (
    <div className={`app${sideOpen ? ' side-open' : ''}${collapsed ? ' side-collapsed' : ''}`}>
      <Sidebar
        me={me.data}
        route={route}
        collapsed={collapsed && !sideOpen}
        onToggle={toggleSide}
        onSettings={() => setSettings(true)}
        onNavigate={() => setSideOpen(false)}
      />
      <div className="side-backdrop" onClick={() => setSideOpen(false)} />
      <div className="mainwrap">
        <header className="top">
          <span className="burger">
            <IconButton icon={<ListIcon size={20} />} label="Otevřít menu" variant="ghost" onClick={() => setSideOpen(true)} />
          </span>
          <TopTitle key={route.t === 'home' || route.t === 'transcripts' ? route.t : route.id} title={title} onRename={rename} />
          <span className="grow" />
          {me.data.ai !== 'ok' && <span className="ai-pill">AI nedostupná</span>}
          <IconButton icon={<MagnifyingGlassIcon size={18} />} label="Hledat" variant="ghost" onClick={() => setSearch(true)} />
          <IconButton icon={<ChatCircleIcon size={19} />} label="Nový chat" variant="ghost" onClick={() => navigate('/')} />
        </header>
        <main>
          {route.t === 'chat' ? (
            <Chat key={route.id} id={route.id} me={me.data} />
          ) : route.t === 'view' ? (
            <View key={route.id} id={route.id} me={me.data} />
          ) : route.t === 'transcripts' ? (
            <Transcripts slug={route.id} me={me.data} />
          ) : (
            <Home me={me.data} />
          )}
        </main>
      </div>
      <SettingsModal open={settings} onClose={() => setSettings(false)} me={me.data} />
      <Search open={search} onClose={() => setSearch(false)} />
    </div>
  );
}
